import { constants, useBreakpointRange } from "@newsela/angelou";
import { useEffect } from "react";

import { setAssignmentMember } from "@/assignments/utils/assignmentActions";
import { BannerMode } from "@/types/BannerMode.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import AssignAction from "@/view/AssignAction";
import { ISavingState } from "@/view/AssignActionsContainer/AssignActionsContainer.interface";
import AssignmentStatusBadge from "@/view/AssignmentStatusBadge";
import contentTypes from "@/view/constants/contentTypes";
import uiTypes from "@/view/constants/uiTypes";
import CopyButton from "@/view/CopyButton";
import StickyBar from "@/view/StickyBar";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import {
  actionsToolbarCss,
  assignAndStatusWrapperCss,
  assignmentActionsWrapperCss,
  assignPrintAndMoreWrapperCss,
  assignStatusAndStudentViewCss,
  showForDesktopCss,
  showForMobileCss,
} from "./UnlevelledContentActionsToolbar.style";
import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

interface UnlevelledContentActionsToolbarInterface {
  assignModalTriggerRef: React.Ref<HTMLButtonElement> | null;
  bannerMode: BannerMode;
  content: IUnlevelledContent;
  dispatch: () => void;
  handlePublish: () => void;
  isSaving: ISavingState;
  currentAssignment: Assignment;
  setAdvisoryModalActive: (a: boolean) => void;
  setIsAssignModalActive: (a: boolean) => void;
  setIsOpeningAssignmentSettings: (a: boolean) => void;
  updatedAssignment: Assignment;
  userProperties: UserProperties;
}

export const UnlevelledContentActionsToolbar = ({
  assignModalTriggerRef,
  bannerMode,
  content,
  dispatch,
  handlePublish,
  isSaving,
  currentAssignment,
  setAdvisoryModalActive,
  setIsAssignModalActive,
  setIsOpeningAssignmentSettings,
  updatedAssignment,
  userProperties,
}: UnlevelledContentActionsToolbarInterface) => {
  const { isTeacher, isStudent, isLiteUser } = userProperties;
  const { uiType } = useUserContentUIContext();
  const { isBannerModeActive } = bannerMode;
  const { id: contentId, content_type: contentType } = content;
  const liteUserLiteContent = uiType === uiTypes.LITE;
  const showPremiumActions = !isLiteUser || liteUserLiteContent || isStudent;

  useEffect(() => {
    const { assignment_members: assignmentMembers } = updatedAssignment;

    if (assignmentMembers && assignmentMembers.length === 0) {
      setAssignmentMember(contentId, dispatch);
    }
  }, [contentId]);

  const isTuvaDataset = contentType === contentTypes.TUVA_DATASET;
  const showAssignAction =
    isTeacher && showPremiumActions && !isBannerModeActive && !isTuvaDataset;

  const {
    breakpoints: {
      BreakpointRanges: { xsToSm, zeroToXs, smToMd, lgToInfinity },
    },
  } = constants;
  const currentViewportRange = useBreakpointRange();
  const isMobile = currentViewportRange !== lgToInfinity;
  const useMobileAssignActions = [zeroToXs, xsToSm, smToMd].includes(
    currentViewportRange,
  );

  const renderAssignAction = () => {
    if (!showAssignAction) return null;

    return (
      <AssignAction
        content={content as IUnlevelledContent}
        handlePublish={handlePublish}
        isMobile={useMobileAssignActions}
        isSaving={isSaving.value}
        originalAssignment={currentAssignment}
        setAdvisoryModalActive={setAdvisoryModalActive}
        setIsOpeningAssignmentSettings={setIsOpeningAssignmentSettings}
        triggerRef={assignModalTriggerRef}
      />
    );
  };

  const renderCopyButton = () => {
    return (
      <CopyButton
        contentId={contentId}
        iconOnly={isMobile}
        contentType={contentType}
        isStudent={isStudent}
        setIsAssignModalActive={setIsAssignModalActive}
        triggerRef={assignModalTriggerRef}
        uiType={uiType}
      />
    );
  };

  return (
    <StickyBar>
      <div css={actionsToolbarCss}>
        <div css={showForMobileCss}>
          <div css={assignStatusAndStudentViewCss}>
            <div css={assignAndStatusWrapperCss}>
              <AssignmentStatusBadge />
              {renderAssignAction()}
            </div>
          </div>
          <div css={assignmentActionsWrapperCss}>{renderCopyButton()}</div>
        </div>
        <div css={showForDesktopCss}>
          <div css={assignPrintAndMoreWrapperCss}>
            <AssignmentStatusBadge />
            {renderCopyButton()}
            {renderAssignAction()}
          </div>
        </div>
      </div>
    </StickyBar>
  );
};

export default UnlevelledContentActionsToolbar;
